import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Leaf, Heart } from "lucide-react";
import { useApp } from "../AppContext";
import DominicanFlag from "./DominicanFlag";
import { PalmLeaf } from "./DominicanArt";
import SwingingFlag from "./SwingingFlag";
import DevBranding from "./DevBranding";
import { FlagBunting, FlagStripe } from "./FlagAccents";

// User-provided chef artwork (Gemini-generated, 2x2 layout)
const CHEF_ARTWORK =
    "https://customer-assets.emergentagent.com/job_gia-milly-eats/artifacts/9rg3om0a_Gemini_Generated_Image_uctyhructyhructy.png";

const HERO_BG =
    "https://images.unsplash.com/photo-1564132254865-639bfec4c5bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHx0cm9waWNhbCUyMGxlYXZlcyUyMHN1biUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc4NDUwNTY3fDA&ixlib=rb-4.1.0&q=85";

// Top-right quadrant of the artwork (Milly closeup)
const chefHeroStyle = {
    backgroundImage: `url(${CHEF_ARTWORK})`,
    backgroundSize: "200% 200%",
    backgroundPosition: "100% 0%",
    backgroundRepeat: "no-repeat",
};

const Hero = () => {
    const { t, lang, branding } = useApp();
    // When the admin only filled in one language in Settings, fall back to the
    // default i18n string for the OTHER language so the page never shows
    // blank text after a language toggle.
    const heroHeadline =
        (lang === "es" ? branding.hero_headline_es : branding.hero_headline_en) ||
        `${t.hero.title} ${t.hero.titleAccent}`;
    const heroSubtitle =
        (lang === "es" ? branding.hero_subtitle_es : branding.hero_subtitle_en) ||
        t.hero.subtitle;
    const ctaPrimary =
        (lang === "es" ? branding.hero_cta_primary_es : branding.hero_cta_primary_en) ||
        t.hero.cta;
    const ctaSecondary =
        (lang === "es" ? branding.hero_cta_secondary_es : branding.hero_cta_secondary_en) ||
        t.hero.cta2;
    const chefName = branding.chef_name || "Milly";
    const cuisineLabel =
        lang === "es"
            ? branding.country_preset?.cuisine_label_es
            : branding.country_preset?.cuisine_label_en;
    const heroImage = branding.hero_image_url || CHEF_ARTWORK;
    const useArtworkCrop = !branding.hero_image_url;
    const chefHeroStyleDynamic = useArtworkCrop
        ? chefHeroStyle
        : {
              backgroundImage: `url(${heroImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
          };

    const scrollTo = (id) =>
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

    return (
        <section
            id="home"
            data-testid="hero-section"
            className="relative min-h-screen flex items-center pt-44 sm:pt-48 lg:pt-32 pb-16 overflow-hidden grain"
        >
            {/* Background tropical pattern */}
            <div className="absolute inset-0 z-0">
                <img
                    src={HERO_BG}
                    alt=""
                    className="w-full h-full object-cover opacity-25 animate-slow-zoom"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-earth-50 via-earth-50/80 to-earth-100" />
            </div>

            {/* Swinging flag — country-aware, hidden when decorations are off */}
            {branding.decorations_enabled && (
                <>
                    <div className="absolute top-4 right-[6%] z-20 hidden md:block">
                        <SwingingFlag size="xl" />
                    </div>
                    <div className="absolute top-24 right-4 z-20 md:hidden">
                        <SwingingFlag size="md" />
                    </div>
                    {/* Festive bunting across the very top */}
                    <FlagBunting
                        count={28}
                        className="absolute top-0 left-0 right-0 z-10 px-4 pointer-events-none"
                    />
                    {/* Bottom tricolor stripe */}
                    <FlagStripe
                        thickness="h-1.5"
                        className="absolute bottom-0 left-0 right-0 z-10"
                    />
                </>
            )}

            {/* palm leaves — toggle with decorations */}
            {branding.decorations_enabled && (
                <>
                    <PalmLeaf
                        className="absolute -left-12 -top-8 w-44 opacity-30 rotate-12 hidden md:block pointer-events-none"
                        color="#4A7C59"
                    />
                    <PalmLeaf
                        className="absolute -right-16 bottom-0 w-56 opacity-20 -rotate-12 hidden lg:block pointer-events-none"
                        color="#3A6347"
                    />
                    <div
                        aria-hidden
                        className="absolute top-1/3 right-8 w-2 h-32 rounded-full hidden lg:block"
                        style={{
                            background:
                                "linear-gradient(to bottom, var(--flag-1), var(--flag-2), var(--flag-3))",
                        }}
                    />
                </>
            )}

            <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center w-full">
                {/* Left content */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="lg:col-span-7"
                >
                    <span className="pill-tag mb-6" data-testid="hero-tag">
                        {branding.decorations_enabled && (
                            <DominicanFlag className="w-5 h-3.5 rounded-sm" />
                        )}
                        {cuisineLabel || t.hero.tag}
                    </span>

                    <h1
                        data-testid="hero-title"
                        className="font-serif font-black text-earth-900 text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.05] tracking-tight text-balance"
                    >
                        {heroHeadline}
                    </h1>

                    <p
                        data-testid="hero-subtitle"
                        className="mt-7 text-lg sm:text-xl text-earth-700 leading-relaxed max-w-2xl"
                    >
                        {heroSubtitle}
                    </p>

                    <div className="mt-9 flex flex-wrap gap-4">
                        <button
                            data-testid="hero-cta-order-btn"
                            onClick={() => scrollTo("order")}
                            style={{ background: branding.brand_color_primary }}
                            className="btn-primary group !bg-[var(--brand-primary)]"
                        >
                            {ctaPrimary}
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                        <button
                            data-testid="hero-cta-menu-btn"
                            onClick={() => scrollTo("menu")}
                            className="btn-secondary"
                        >
                            {ctaSecondary}
                        </button>
                    </div>

                    <div className="mt-12 flex flex-wrap gap-6">
                        {[
                            { icon: Heart, label: (lang === "es" ? branding.hero_badge1_es : branding.hero_badge1_en) || t.hero.badge1 },
                            { icon: Leaf, label: (lang === "es" ? branding.hero_badge2_es : branding.hero_badge2_en) || t.hero.badge2 },
                            { icon: Sparkles, label: (lang === "es" ? branding.hero_badge3_es : branding.hero_badge3_en) || t.hero.badge3 },
                        ].map(({ icon: Icon, label }, i) => (
                            <div key={`hero-badge-${i}`} className="flex items-center gap-2.5 text-earth-700">
                                <div
                                    className="w-9 h-9 rounded-full flex items-center justify-center"
                                    style={{ background: `${branding.brand_color_primary}1A` }}
                                >
                                    <Icon className="w-4 h-4" style={{ color: branding.brand_color_primary }} />
                                </div>
                                <span className="text-sm font-semibold">{label}</span>
                            </div>
                        ))}
                    </div>
                </motion.div>

                {/* Right visual: CHEFS together (from artwork) */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.92 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
                    className="lg:col-span-5 relative"
                >
                    <div className="relative aspect-[4/5] w-full max-w-md mx-auto">
                        {/* Decorative ring */}
                        <div className="absolute -inset-6 rounded-full border border-terracotta-300/40" />
                        <div className="absolute -inset-12 rounded-full border border-leaf-500/30" />

                        <div
                            className="relative h-full w-full rounded-[40px] overflow-hidden shadow-2xl"
                            style={chefHeroStyleDynamic}
                            data-testid="hero-chefs-image"
                            aria-label={`Chef ${chefName}`}
                        >
                            <div className="absolute inset-0 bg-gradient-to-t from-earth-900/45 via-transparent to-transparent" />
                            {/* Mini flag corner accent */}
                            {branding.decorations_enabled && (
                                <div className="absolute top-5 left-5 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur flex items-center gap-2 shadow-md">
                                    <DominicanFlag className="w-5 h-3.5 rounded-sm" />
                                    <span className="text-[10px] uppercase tracking-wider font-bold text-earth-900">
                                        {cuisineLabel}
                                    </span>
                                </div>
                            )}
                            {/* Names overlay */}
                            <div className="absolute bottom-5 left-5 right-5 text-earth-50">
                                <div
                                    className="font-accent text-3xl -mb-1"
                                    style={{ color: branding.brand_color_primary, opacity: 0.85 }}
                                >
                                    Chef
                                </div>
                                <h3 className="font-serif font-black text-3xl drop-shadow">
                                    {chefName}
                                </h3>
                            </div>
                        </div>

                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            className="absolute -left-4 sm:-left-12 bottom-12 bg-white/95 backdrop-blur-md rounded-2xl px-5 py-4 shadow-xl border border-earth-100 max-w-[220px]"
                        >
                            <div className="font-accent text-terracotta-600 text-2xl leading-none">
                                ¡Buen provecho!
                            </div>
                            <p className="text-xs text-earth-700 mt-1.5 leading-snug">
                                Cocinado con amor por Chef Milly
                            </p>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.8 }}
                            className="absolute -right-4 sm:-right-8 top-8 bg-earth-900 text-earth-50 rounded-2xl px-4 py-3 shadow-xl flex items-center gap-2"
                        >
                            <div className="w-2 h-2 rounded-full bg-leaf-400 animate-pulse" />
                            <span className="text-xs font-semibold uppercase tracking-wider">
                                Montego Bay
                            </span>
                        </motion.div>
                    </div>
                </motion.div>
            </div>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-30">
                <DevBranding variant="hero" />
            </div>
        </section>
    );
};

export default Hero;
